import React, { createContext, useContext, useState, ReactNode } from 'react';
import { uploadService } from '../services/upload.service';
import { projectService } from '../services/project.service';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

interface UploadState {
    progress: number;
    status: 'idle' | 'uploading' | 'completed' | 'error';
    file: File | null;
    projectId: string | null;
    errorMessage?: string;
}

interface UploadContextType {
    uploadState: UploadState;
    startUpload: (projectId: string, file: File, onComplete?: (url: string) => void) => Promise<void>;
    clearUpload: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [uploadState, setUploadState] = useState<UploadState>({
        progress: 0,
        status: 'idle',
        file: null,
        projectId: null,
    });

    const startUpload = async (projectId: string, file: File, onComplete?: (url: string) => void) => {
        try {
            setUploadState({ progress: 0, status: 'uploading', file, projectId });

            const cloudUrl = await uploadService.uploadVideo(
                projectId,
                file,
                (progress) => {
                    setUploadState((prev) => ({ ...prev, progress }));
                }
            );

            setUploadState((prev) => ({ ...prev, status: 'completed', progress: 100 }));
            if (onComplete) onComplete(cloudUrl);

            // Auto clear after 3 seconds
            setTimeout(() => {
                setUploadState((prev) => {
                    if (prev.status === 'completed') {
                        return { progress: 0, status: 'idle', file: null, projectId: null };
                    }
                    return prev;
                });
            }, 3000);

        } catch (err: any) {
            setUploadState((prev) => ({
                ...prev,
                status: 'error',
                errorMessage: err.message || 'Upload failed'
            }));
        }
    };

    const clearUpload = () => {
        setUploadState({ progress: 0, status: 'idle', file: null, projectId: null });
    };

    // Prevent closing tab while uploading
    React.useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (uploadState.status === 'uploading') {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [uploadState.status]);

    return (
        <UploadContext.Provider value={{ uploadState, startUpload, clearUpload }}>
            {children}

            {/* Global Upload Toast/Widget */}
            {uploadState.status !== 'idle' && (
                <div className="fixed bottom-6 right-6 z-[9999] bg-white rounded-2xl shadow-2xl shadow-slate-200/50 border border-slate-100 p-4 w-80 font-sans transform transition-all duration-300 animate-in slide-in-from-bottom-5">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                            {uploadState.status === 'uploading' && (
                                <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                    <Loader2 size={16} className="animate-spin" />
                                </div>
                            )}
                            {uploadState.status === 'completed' && (
                                <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                    <CheckCircle size={16} />
                                </div>
                            )}
                            {uploadState.status === 'error' && (
                                <div className="w-8 h-8 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">
                                    <XCircle size={16} />
                                </div>
                            )}
                            <div>
                                <p className="text-sm font-bold text-slate-900 leading-tight">
                                    {uploadState.status === 'uploading' ? 'Uploading Video' :
                                        uploadState.status === 'completed' ? 'Upload Complete' : 'Upload Failed'}
                                </p>
                                <p className="text-xs text-slate-500 font-medium truncate max-w-[150px]">
                                    {uploadState.file?.name || 'Unknown file'}
                                </p>
                            </div>
                        </div>

                        {(uploadState.status === 'completed' || uploadState.status === 'error') && (
                            <button onClick={clearUpload} className="text-slate-400 hover:text-slate-600">
                                <XCircle size={16} />
                            </button>
                        )}
                    </div>

                    {uploadState.status === 'uploading' && (
                        <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                                    style={{ width: `${uploadState.progress}%` }}
                                />
                            </div>
                            <span className="text-xs font-bold text-slate-600 tabular-nums min-w-[32px]">
                                {uploadState.progress}%
                            </span>
                        </div>
                    )}

                    {uploadState.status === 'error' && (
                        <p className="text-xs text-rose-600 font-medium">
                            {uploadState.errorMessage}
                        </p>
                    )}
                </div>
            )}
        </UploadContext.Provider>
    );
};

export const useGlobalUpload = () => {
    const context = useContext(UploadContext);
    if (context === undefined) {
        throw new Error('useGlobalUpload must be used within an UploadProvider');
    }
    return context;
};
